import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  FiShield,
  FiUser,
  FiHome,
  FiAlertTriangle,
  FiCheckSquare,
  FiMessageCircle,
  FiFlag,
  FiLink,
  FiPhone,
  FiEye,
  FiDollarSign,
  FiCheckCircle,
} from "react-icons/fi";
import Header from "../../components/layouts/Header";
import Footer from "../../components/layouts/Footer";
import ContactStrip from "./ContactStrip";
import "./FooterPages.css";

const TABS = [
  { id: "intro", label: "Introduction", icon: <FiShield /> },
  { id: "buyer", label: "For a Buyer / Investor", icon: <FiUser /> },
  { id: "tenant", label: "For a Tenant", icon: <FiHome /> },
  { id: "fraud", label: "How to Avoid Real Estate Fraud", icon: <FiAlertTriangle /> },
  { id: "checklist", label: "Property Verification Checklist", icon: <FiCheckSquare /> },
  { id: "communication", label: "Safe Communication Tips", icon: <FiMessageCircle /> },
  { id: "reporting", label: "Reporting Issues", icon: <FiFlag /> },
  { id: "contact", label: "Contact Us", icon: <FiPhone /> },
];

const SafetyGuide = () => {
  const [activeTab, setActiveTab] = useState("intro");

  const scrollToTab = (id) => {
    setActiveTab(id);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div className="mgh-info-page-wrapper">
      <Helmet>
        <title>Safety Guide | My Golden Homes</title>
        <meta
          name="description"
          content="Your safety matters! Follow these essential tips to make your property search safer, smarter, and scam-free on My Golden Homes."
        />
      </Helmet>

      {/* Unchanged Header */}
      <Header />

      {/* Hero Banner */}
      <section className="mgh-info-hero">
        <div className="mgh-info-hero-inner">
          <div className="mgh-info-hero-left">
            <div className="mgh-info-breadcrumb">
              <Link to="/">Home</Link>
              <span>&gt;</span>
              <span>Safety Guide</span>
            </div>
            <h1 className="mgh-info-hero-title">
              Safety <span>Guide</span>
            </h1>
            <p className="mgh-info-hero-subtitle">
              Your safety matters! Follow these important tips to make your property search safer and smarter.
            </p>

            <div className="mgh-hero-pills-row">
              <div className="mgh-hero-pill">
                <div className="mgh-hero-pill-icon">
                  <FiShield />
                </div>
                <div className="mgh-hero-pill-text">
                  <strong>Fraud Protection</strong>
                  <span>Best Safety Practices</span>
                </div>
              </div>
              <div className="mgh-hero-pill">
                <div className="mgh-hero-pill-icon">
                  <FiCheckCircle />
                </div>
                <div className="mgh-hero-pill-text">
                  <strong>Verified Listings</strong>
                  <span>Direct Owner Connect</span>
                </div>
              </div>
            </div>
          </div>

          <div className="mgh-info-hero-art">
            <img
              src="/assets/images/villa_header_banner.jpg"
              alt="My Golden Homes Safety Guide"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/assets/images/why_choose_villa.jpg";
              }}
            />
          </div>
        </div>
      </section>

      {/* Main Container */}
      <div className="mgh-info-container">
        <div className="mgh-info-grid-sidebar">
          {/* Left Sticky Sidebar */}
          <aside className="mgh-sidebar-nav-card">
            {TABS.map((tab) => (
              <button
                type="button"
                key={tab.id}
                className={`mgh-sidebar-nav-item ${activeTab === tab.id ? "active" : ""}`}
                onClick={() => scrollToTab(tab.id)}
              >
                <span className="mgh-sidebar-nav-icon">{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </aside>

          {/* Right Content */}
          <div className="mgh-info-main-content">
            {/* Introduction & Beware */}
            <section id="intro" className="mgh-info-section">
              <div
                style={{
                  background: "#f0fdf4",
                  border: "1px solid #bbf7d0",
                  borderRadius: "10px",
                  padding: "16px 20px",
                  display: "flex",
                  gap: "14px",
                  alignItems: "center",
                  marginBottom: "20px",
                }}
              >
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "50%",
                    background: "#16a34a",
                    color: "#ffffff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "20px",
                    flexShrink: 0,
                  }}
                >
                  <FiShield />
                </div>
                <div>
                  <h3 style={{ margin: "0 0 2px 0", fontSize: "16px", fontWeight: "700", color: "#14532d" }}>
                    Safety Guide
                  </h3>
                  <p style={{ margin: 0, fontSize: "13px", color: "#166534" }}>
                    At MyGoldenHomes, your safety and trust are important to us. This guide provides useful tips and information to help you make safe and informed decisions while buying, selling or renting a property.
                  </p>
                </div>
              </div>

              <h2 style={{ fontSize: "19px", fontWeight: "800", color: "#0f172a", margin: "0 0 10px 0" }}>
                Beware - Use your Judgment
              </h2>
              <p className="mgh-info-text">
                It is in the interest of a user to exercise his/her best judgment to assess properties and owners&apos; authenticity. The claims being made may or may not be correct, so users are advised to independently verify the authenticity of the claims being made. Mygoldenhomes.com is an advertising medium and does not verify the content uploaded by advertisers. We recommend that all our users go through the checklist mentioned below prior to purchasing or renting a house.
              </p>

              {/* 4 Key Tips for a Safer Experience */}
              <div
                style={{
                  background: "#fffbeb",
                  border: "1px solid #fde68a",
                  borderRadius: "12px",
                  padding: "20px",
                  margin: "24px 0",
                }}
              >
                <h4 style={{ margin: "0 0 16px 0", fontSize: "16px", fontWeight: "800", color: "#92400e" }}>
                  Key Tips for a Safer Experience
                </h4>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                    gap: "14px",
                  }}
                >
                  <div style={{ background: "#ffffff", padding: "14px", borderRadius: "8px", border: "1px solid #fef3c7" }}>
                    <div style={{ color: "#d97706", fontSize: "20px", marginBottom: "6px" }}><FiCheckCircle /></div>
                    <strong style={{ display: "block", fontSize: "13.5px", color: "#0f172a", marginBottom: "3px" }}>
                      Verify Authenticity
                    </strong>
                    <span style={{ fontSize: "12px", color: "#64748b" }}>
                      Check property documents, RERA approvals, and owner identity.
                    </span>
                  </div>

                  <div style={{ background: "#ffffff", padding: "14px", borderRadius: "8px", border: "1px solid #fef3c7" }}>
                    <div style={{ color: "#0284c7", fontSize: "20px", marginBottom: "6px" }}><FiEye /></div>
                    <strong style={{ display: "block", fontSize: "13.5px", color: "#0f172a", marginBottom: "3px" }}>
                      Visit Property in Person
                    </strong>
                    <span style={{ fontSize: "12px", color: "#64748b" }}>
                      Always inspect the physical site and neighborhood before making payment.
                    </span>
                  </div>

                  <div style={{ background: "#ffffff", padding: "14px", borderRadius: "8px", border: "1px solid #fef3c7" }}>
                    <div style={{ color: "#059669", fontSize: "20px", marginBottom: "6px" }}><FiDollarSign /></div>
                    <strong style={{ display: "block", fontSize: "13.5px", color: "#0f172a", marginBottom: "3px" }}>
                      Avoid Advance Payments
                    </strong>
                    <span style={{ fontSize: "12px", color: "#64748b" }}>
                      Do not transfer booking deposits before executing legal agreements.
                    </span>
                  </div>

                  <div style={{ background: "#ffffff", padding: "14px", borderRadius: "8px", border: "1px solid #fef3c7" }}>
                    <div style={{ color: "#e11d48", fontSize: "20px", marginBottom: "6px" }}><FiAlertTriangle /></div>
                    <strong style={{ display: "block", fontSize: "13.5px", color: "#0f172a", marginBottom: "3px" }}>
                      Be Cautious of Scams
                    </strong>
                    <span style={{ fontSize: "12px", color: "#64748b" }}>
                      Report any suspicious demands for gate passes or OTP codes immediately.
                    </span>
                  </div>
                </div>
              </div>
            </section>

            {/* Buyer Section */}
            <section id="buyer" className="mgh-info-section">
              <h2 className="mgh-info-section-title">
                <FiUser /> For a Buyer / Investor
              </h2>
              <p className="mgh-info-text">
                The buyer should conduct checks and establish authenticity of any property, or project, its title, built up area (in standardized unit), suitability for buying in a form and manner deemed appropriate at his or her COST. Mygoldenhomes.com being an advertising medium does not and cannot vouch for authenticity of content on its site.
              </p>
              <ul style={{ paddingLeft: "20px", fontSize: "14px", color: "#334155", lineHeight: "1.6" }}>
                <li>Confirm RERA registration number of the project on the state RERA portal.</li>
                <li>Verify title deed and ownership chain for at least the past 30 years.</li>
                <li>Obtain an Encumbrance Certificate (EC) to ensure no bank mortgage or litigation exists.</li>
                <li>Confirm sanctioned building plans from municipal authorities (GHMC, HMDA, etc.).</li>
              </ul>
            </section>

            {/* Tenant Section */}
            <section id="tenant" className="mgh-info-section">
              <h2 className="mgh-info-section-title">
                <FiHome /> For a Tenant
              </h2>
              <p className="mgh-info-text">
                Tenants must always visit the apartment, builder floor, or villa in person, meet the owner or authorized caretaker, and inspect maintenance, water, electricity meters, and parking allocations before agreeing to lease terms.
              </p>
            </section>

            {/* Avoid Fraud */}
            <section id="fraud" className="mgh-info-section">
              <h2 className="mgh-info-section-title">
                <FiAlertTriangle /> How to Avoid Real Estate Fraud
              </h2>
              <p className="mgh-info-text">
                Be vigilant against common online scams such as "Army Officer transfer", "Visiting gate pass fee", or demands for online UPI transfers before scheduling visits. My Golden Homes will <strong>never</strong> ask you to pay money to unlock visit passes or view phone numbers.
              </p>
            </section>

            {/* Checklist */}
            <section id="checklist" className="mgh-info-section">
              <h2 className="mgh-info-section-title">
                <FiCheckSquare /> Property Verification Checklist
              </h2>
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {[
                  "Verify identity of the seller/landlord (Aadhaar, Passport, PAN).",
                  "Ensure all property taxes and society maintenance bills are cleared up to date.",
                  "Execute registered rental agreement or registered sale deed on official stamp paper.",
                  "Verify that bank accounts for payment match the legal property owner's name.",
                ].map((item, i) => (
                  <div key={i} style={{ display: "flex", gap: "10px", alignItems: "flex-start", fontSize: "13.5px", color: "#334155" }}>
                    <FiCheckCircle style={{ color: "#059669", marginTop: "3px", flexShrink: 0 }} />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* Communication */}
            <section id="communication" className="mgh-info-section">
              <h2 className="mgh-info-section-title">
                <FiMessageCircle /> Safe Communication Tips
              </h2>
              <p className="mgh-info-text">
                Communicate through our official platform messaging or direct verified phone numbers provided on property pages. Never share your bank passwords, UPI PINs, or confidential SMS verification OTPs with anyone.
              </p>
            </section>

            {/* Reporting */}
            <section id="reporting" className="mgh-info-section">
              <h2 className="mgh-info-section-title">
                <FiFlag /> Reporting Suspicious Issues
              </h2>
              <p className="mgh-info-text">
                If you encounter any misleading advertisement, scam attempt, or fraudulent user, please report it immediately via our <Link to="/feedback" style={{ color: "#0284c7", fontWeight: "700" }}>Feedback Desk</Link> or <Link to="/summonsnotices" style={{ color: "#0284c7", fontWeight: "700" }}>Grievance Form</Link>.
              </p>
            </section>
          </div>
        </div>

        {/* Contact Strip */}
        <ContactStrip />
      </div>

      {/* Unchanged Footer */}
      <Footer />
    </div>
  );
};

export default SafetyGuide;